<template>
  <div class="max-w-2xl mx-auto px-5 py-8">
    <nav class="text-sm text-gray-500 dark:text-gray-400">
      <a :href="backUrl" class="hover:text-emerald-700 hover:underline">Home</a>
      <span class="mx-1.5">/</span>
      <a :href="unitUrl" class="hover:text-emerald-700 hover:underline">{{ word.unit_title }}</a>
    </nav>

    <header class="mt-5 flex items-start gap-4 flex-wrap">
      <h1 class="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50">
        {{ word.vietnamese }}
      </h1>
      <AudioButton v-if="word.audio" :src="word.audio" :show-slow="true" class="mt-2" />
    </header>

    <p class="mt-2 text-lg text-gray-600 dark:text-gray-300">{{ word.english }}</p>

    <div class="mt-2 flex items-center gap-3 flex-wrap text-sm">
      <span v-if="word.phonetic" class="font-medium text-emerald-700 dark:text-emerald-400">{{ word.phonetic }}</span>
      <span v-if="word.ipa" class="text-gray-400 dark:text-gray-500">/{{ word.ipa.replaceAll('/', '') }}/</span>
      <span class="text-xs uppercase tracking-wide bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded">
        {{ word.part_of_speech }}
      </span>
    </div>

    <div v-if="word.southern_note"
         class="mt-5 bg-amber-50 dark:bg-amber-950 border border-amber-100 dark:border-amber-900 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-xl text-sm">
      <span class="font-semibold">Southern note:</span> {{ word.southern_note }}
    </div>

    <!-- Tone curve. The pitch track is the whole reason this page exists rather
         than a tooltip: a learner can see the shape before trying to match it. -->
    <section class="mt-8">
      <h2 class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Tone curve</h2>
      <div class="mt-2">
        <p v-if="curveState === 'loading'" class="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">
          Reading the pitch&hellip;
        </p>
        <p v-else-if="curveState === 'error'"
           class="text-sm text-gray-400 dark:text-gray-500 py-8 text-center rounded-lg border border-gray-200 dark:border-gray-700">
          No pitch track available for this clip.
        </p>
        <ToneContour v-else :reference="curve" :reference-label="`${dialectLabel} reference`" />
      </div>
    </section>

    <!-- Practice. Course words are quota-limited for free accounts and unlimited
         for Pro — the same rule the tone-practice tool already applies, so this
         links there rather than inventing a second gate. -->
    <section class="mt-6">
      <a v-if="subscribed"
         :href="`${practiceUrl}?word=${word.id}`"
         class="w-full inline-flex items-center justify-center gap-2 font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl shadow-sm transition-colors">
        Practise this tone
      </a>
      <div v-else class="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-5 py-4">
        <p class="text-sm text-emerald-900 dark:text-emerald-100 font-medium">Tone practice is a Pro feature.</p>
        <p class="mt-1 text-sm text-emerald-800 dark:text-emerald-200">
          Record yourself and see your pitch against the reference.
        </p>
        <a :href="`${practiceUrl}?word=${word.id}`"
           class="mt-3 inline-flex items-center font-semibold text-emerald-700 dark:text-emerald-400 hover:underline text-sm">
          See how it works →
        </a>
      </div>
    </section>

    <section v-if="word.example_vn" class="mt-8">
      <h2 class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Example</h2>
      <div class="mt-2 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
        <div class="flex items-center gap-3">
          <p class="text-lg font-semibold text-gray-900 dark:text-gray-50">{{ word.example_vn }}</p>
          <AudioButton v-if="word.example_audio" :src="word.example_audio" :show-slow="true" />
        </div>
        <p class="mt-1 text-gray-500 dark:text-gray-400">{{ word.example_en }}</p>
      </div>
    </section>

    <!-- Everything we already track about this learner and this word. -->
    <section class="mt-8">
      <h2 class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Your progress</h2>
      <div class="mt-2 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
        <template v-if="review">
          <div class="flex items-center gap-3">
            <StrengthDots :strength="review.strength" />
            <span class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ strengthLabel }}</span>
            <span v-if="review.due"
                  class="text-xs font-semibold bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded">
              due now
            </span>
          </div>
          <dl class="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div class="flex justify-between"><dt class="text-gray-500 dark:text-gray-400">Times reviewed</dt>
              <dd class="font-medium text-gray-800 dark:text-gray-100">{{ review.repetitions }}</dd></div>
            <div class="flex justify-between"><dt class="text-gray-500 dark:text-gray-400">Interval</dt>
              <dd class="font-medium text-gray-800 dark:text-gray-100">{{ review.interval_days }}d</dd></div>
            <div class="flex justify-between"><dt class="text-gray-500 dark:text-gray-400">Next review</dt>
              <dd class="font-medium text-gray-800 dark:text-gray-100">{{ fmt(review.next_review_at) }}</dd></div>
            <div class="flex justify-between"><dt class="text-gray-500 dark:text-gray-400">Last seen</dt>
              <dd class="font-medium text-gray-800 dark:text-gray-100">{{ fmt(review.last_reviewed_at) }}</dd></div>
          </dl>
        </template>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
          You haven't started this word yet — it'll enter your review schedule when you begin
          <a :href="unitUrl" class="text-emerald-700 dark:text-emerald-400 font-medium hover:underline">{{ word.unit_title }}</a>.
        </p>

        <button type="button" @click="toggleSaved" :disabled="savePending"
                class="mt-5 w-full text-sm font-semibold px-4 py-2.5 rounded-xl border transition-colors disabled:opacity-50"
                :class="saved
                  ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'">
          {{ saved ? '✓ In your deck' : '+ Add to deck' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import AudioButton from './shared/AudioButton.vue'
import ToneContour from './shared/ToneContour.vue'
import StrengthDots from './shared/StrengthDots.vue'

const props = defineProps({
  word: { type: Object, required: true },
  review: { type: Object, default: null },
  referenceUrl: { type: String, required: true },
  contourUrl: { type: String, required: true },
  deckUrl: { type: String, required: true },
  practiceUrl: { type: String, required: true },
  unitUrl: { type: String, required: true },
  backUrl: { type: String, required: true },
  subscribed: { type: Boolean, default: false },
  dialect: { type: String, default: 'south' },
})

const curveState = ref('loading')
const curve = ref(null)
const saved = ref(Boolean(props.review && props.review.saved))
const savePending = ref(false)

const LABELS = ['New', 'Learning', 'Growing', 'Strong', 'Mastered']
const dialectLabel = props.dialect === 'north' ? 'Hanoi' : 'Saigon'
const strengthLabel = computed(() => LABELS[props.review?.strength ?? 0] || 'New')

function fmt(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

onMounted(async () => {
  if (!props.word.audio) { curveState.value = 'error'; return }
  try {
    const res = await fetch(`${props.referenceUrl}?src=${encodeURIComponent(props.word.audio)}`, {
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) throw new Error(String(res.status))
    const data = await res.json()
    const pts = data?.points || []
    // ToneContour needs at least two points to draw a line; one point is not a
    // contour and renders as an invisible chart rather than an honest "no data".
    if (pts.length < 2) throw new Error('empty')
    curve.value = pts
    curveState.value = 'ready'
  } catch {
    curveState.value = 'error'
  }
})

async function toggleSaved() {
  savePending.value = true
  try {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const opts = { headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token } }
    const res = saved.value
      ? await fetch(`${props.deckUrl}/${props.word.id}`, { method: 'DELETE', ...opts })
      : await fetch(props.deckUrl, {
          method: 'POST', ...opts,
          body: JSON.stringify({ vocabulary_word_id: props.word.id, source_kind: 'word_page' }),
        })
    if (res.ok) saved.value = !saved.value
  } finally {
    savePending.value = false
  }
}
</script>
